/**
 * Several masters per asset (1.2.0): full original files of the same content in other resolutions or editorial
 * variants (UHD and HD, Textless, Clean feed, M&E, an audio description mix).
 *
 * The version's representation of kind `original` stays the primary master; further masters are representations of
 * kind `master`. Every master has two descriptions:
 *   - its format, derived from the probe and never typed: "UHD 2160p25 · ProRes 422 HQ · 8 ch" (formatOf, formatLabelOf);
 *   - its variant, editorial, from the workspace's short list (Administration → Metadata model → Master variants).
 * A representation with no variant is the workspace's Programme variant.
 *
 * One master per variant and format class: a second "Textless · HD" replaces the first (or is skipped, or refused).
 * A new master must be the same content as the primary (sameContentProblem): the same duration to within a frame
 * when its variant requires the same length (Textless does not), and a compatible frame rate.
 */
import { z } from 'zod';
import type { AssetAvailability, FileLocationDTO } from './assets.ts';
import type { ProbeResult } from './jobs.ts';
import { frameRateLabel, ticksPerFrame, type Rational } from './time.ts';
import { CODEC_LABELS } from './export-formats.ts';
import { EXPORT_PRESETS } from './graphics.ts';

/* ---------------------------------------------------------------- variants */

/**
 * The variants every workspace starts with (seeded by migrations 0080 and 0105 and bootstrap); `key` marks them. The
 * format variants (1.7.0) carry the aspect of a multi-format sequence export's format ('16:9', framing.ts aspectLabel).
 */
export const BUILTIN_MASTER_VARIANTS = [
  { key: 'programme', name: 'Programme', code: 'PGM', sameLengthRequired: true, aspect: null },
  { key: 'textless', name: 'Textless', code: 'TXL', sameLengthRequired: false, aspect: null },
  { key: 'clean_feed', name: 'Clean feed', code: 'CF', sameLengthRequired: true, aspect: null },
  { key: 'international', name: 'International (M&E)', code: 'ME', sameLengthRequired: true, aspect: null },
  { key: 'audio_description', name: 'Audio description mix', code: 'AD', sameLengthRequired: true, aspect: null },
  { key: 'landscape_16x9', name: 'Landscape 16:9', code: '16X9', sameLengthRequired: true, aspect: '16:9' },
  { key: 'vertical_9x16', name: 'Vertical 9:16', code: '9X16', sameLengthRequired: true, aspect: '9:16' },
  { key: 'square_1x1', name: 'Square 1:1', code: '1X1', sameLengthRequired: true, aspect: '1:1' },
  { key: 'portrait_4x5', name: 'Portrait 4:5', code: '4X5', sameLengthRequired: true, aspect: '4:5' },
] as const;
export type BuiltinMasterVariantKey = (typeof BUILTIN_MASTER_VARIANTS)[number]['key'];
/** The variant of a primary master that has none set, and the default everywhere a variant is chosen. */
export const PROGRAMME_VARIANT_KEY: BuiltinMasterVariantKey = 'programme';

export interface MasterVariantDTO {
  id: string;
  /** A built-in variant's key (programme, textless, …); null for one an administrator added. */
  key: BuiltinMasterVariantKey | null;
  name: string;
  /** Short code for file names and badges: TXL, CF, ME… (upper case). */
  code: string;
  /** A master of this variant must match the primary's duration to within a frame (off for Textless). */
  sameLengthRequired: boolean;
  /** 1.7.0: the picture aspect of a format variant ('9:16'); null (or absent) for editorial variants. */
  aspect?: string | null;
  position: number;
  /** Masters of this variant (any asset); a variant in use cannot be deleted. */
  inUse: number;
  createdAt: string;
  updatedAt: string;
}

export const MASTER_VARIANT_CODE = /^[A-Z0-9][A-Z0-9_-]{0,11}$/;

export const MasterVariantBody = z.object({
  name: z.string().trim().min(1).max(60),
  code: z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().regex(MASTER_VARIANT_CODE, 'Use 1–12 letters, digits, - or _')),
  sameLengthRequired: z.boolean().default(true),
});
export type MasterVariantBody = z.infer<typeof MasterVariantBody>;
export const MasterVariantPatch = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  code: z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().regex(MASTER_VARIANT_CODE, 'Use 1–12 letters, digits, - or _')).optional(),
  sameLengthRequired: z.boolean().optional(),
});
export type MasterVariantPatch = z.infer<typeof MasterVariantPatch>;
/** PUT /api/v1/master-variants/order: every variant's id, in the new order. */
export const MasterVariantOrderBody = z.object({ ids: z.array(z.string().uuid()).min(1).max(100) });

/* ---------------------------------------------------------------- formats */

/** Resolution classes, largest first; Audio for a master with no picture. */
export const FORMAT_CLASSES = ['8K', 'UHD', 'HD', 'SD', 'Audio'] as const;
export type FormatClass = (typeof FORMAT_CLASSES)[number];

/**
 * The resolution class of a picture size, by its long and short sides (so portrait sizes class like landscape ones):
 * 8K from 7680 / 4320, UHD from 3840 / 2160, HD from 1280 / 720, SD below; Audio without a picture. The SQL function
 * prism_format_class() (migration 0080) is the same rule; keep them together.
 */
export function formatClassOf(width: number | null | undefined, height: number | null | undefined): FormatClass {
  const w = width ?? 0;
  const h = height ?? 0;
  const long = Math.max(w, h);
  const short = Math.min(w || h, h || w);
  if (long <= 0) return 'Audio';
  if (long >= 7680 || short >= 4320) return '8K';
  if (long >= 3840 || short >= 2160) return 'UHD';
  if (long >= 1280 || short >= 720) return 'HD';
  return 'SD';
}

/** A master's format, from its probe. */
export interface MasterFormat {
  /** "UHD 2160p25 · ProRes 422 HQ · 8 ch" */
  label: string;
  formatClass: FormatClass;
  width: number | null;
  height: number | null;
  scan: 'progressive' | 'interlaced' | null;
  frameRate: Rational | null;
  /** The main stream's codec as shown ("ProRes 422 HQ", "H.264 High"); null when unknown. */
  codec: string | null;
  /** All audio channels together; null without audio. */
  audioChannels: number | null;
}

type ProbeLike = Pick<ProbeResult, 'mediaKind' | 'width' | 'height' | 'frameRate' | 'streams'> & Partial<Pick<ProbeResult, 'scanType'>>;

function codecOf(s: ProbeResult['streams'][number] | undefined): string | null {
  if (!s?.codec) return null;
  const name = CODEC_LABELS[s.codec] ?? s.codec;
  return s.profile && s.profile !== 'unknown' && !/^LC$/.test(s.profile) ? `${name} ${s.profile}` : name;
}

export function formatOf(probe: ProbeLike | null | undefined): MasterFormat {
  if (!probe) return { label: 'Not inspected yet', formatClass: 'Audio', width: null, height: null, scan: null, frameRate: null, codec: null, audioChannels: null };
  const streams = probe.streams ?? [];
  const video = probe.mediaKind === 'audio' ? undefined : streams.find((s) => s.type === 'video');
  const audio = streams.filter((s) => s.type === 'audio');
  const channels = audio.length ? audio.reduce((n, s) => n + (s.channels ?? 0), 0) || null : null;
  const width = video ? probe.width ?? video.width ?? null : null;
  const height = video ? probe.height ?? video.height ?? null : null;
  const formatClass = video ? formatClassOf(width, height) : 'Audio';
  const scan = video ? probe.scanType ?? null : null;
  const frameRate = video ? probe.frameRate ?? null : null;
  const codec = codecOf(video ?? audio[0]);
  const parts: string[] = [];
  if (video && width && height) {
    const lines = Math.min(width, height);
    parts.push(`${formatClass} ${lines}${scan === 'interlaced' ? 'i' : 'p'}${frameRate ? frameRateLabel(frameRate) : ''}`);
  } else {
    parts.push(formatClass);
  }
  if (codec) parts.push(codec);
  if (channels) parts.push(`${channels} ch`);
  return { label: parts.join(' · '), formatClass, width, height, scan, frameRate, codec, audioChannels: channels };
}

/** "UHD 2160p25 · ProRes 422 HQ · 8 ch" */
export function formatLabelOf(probe: ProbeLike | null | undefined): string {
  return formatOf(probe).label;
}

/* ---------------------------------------------------------------- masters */

export interface MasterDTO {
  /** The representation's id. */
  id: string;
  /** The version's original (kind `original`); the others are kind `master`. */
  primary: boolean;
  /** 1.7.0: with the variant's aspect when it is a format variant ("9:16"); null or absent for editorial variants. */
  variant: Pick<MasterVariantDTO, 'id' | 'key' | 'name' | 'code' | 'sameLengthRequired' | 'aspect'>;
  format: MasterFormat;
  /** "Textless · UHD 2160p25 · ProRes 422 HQ · 8 ch": variant, then format (the pickers' wording). */
  label: string;
  filename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  checksumSha256: string | null;
  durationTicks: number | null;
  /** The master's own start timecode (a Textless master keeps its own). */
  startTimecode: string | null;
  /**
   * False for a master whose variant does not require the same length (Textless): the asset's subtitles, markers and
   * timecoded metadata do not apply to it.
   */
  sharedTimeline: boolean;
  availability: AssetAvailability;
  locations: FileLocationDTO[];
  /** How it arrived: upload, watch folder, workflow, check in, API; null for a primary. */
  source: MasterSource | null;
  createdAt: string;
  createdBy: string | null;
}

/** Where a master came from; 1.7.0 'export': a further format of a multi-format sequence export saved as an asset. */
export const MASTER_SOURCES = ['upload', 'watch', 'workflow', 'checkin', 'api', 'export'] as const;
export type MasterSource = (typeof MASTER_SOURCES)[number];

/** What a `master` representation keeps in `attributes` (the primary's probe stays on its version). */
export interface MasterAttributes {
  probe: ProbeResult;
  formatClass: FormatClass;
  formatLabel: string;
  originalFilename: string;
  source: MasterSource;
  createdBy?: string | null;
  automationRunId?: string | null;
  /** Replacing another master of the same variant and format: that master's id, removed once this one is verified. */
  replaces?: string | null;
}

/** The Library's summary of an asset's masters (AssetSummary.masters), from assets.master_tags. */
export interface MasterSummaryDTO {
  /** Masters of the current version, the primary included. */
  count: number;
  /** Format classes present, largest first ("UHD", "HD"). */
  formats: FormatClass[];
  /** Variants present (ids; resolve names and codes with the variant list), in the list's order where known. */
  variantIds: string[];
}

/**
 * assets.master_tags (migration 0080): one tag per variant (`v:<variant id>`), per format class (`f:<class>`), and per
 * variant and format pair (`m:<variant id>:<class>`) of the current version's masters, the primary included. The
 * Library's `masters` filter takes these tags: values with the same prefix are alternatives (OR), the groups must all
 * hold (AND).
 */
export const MASTER_TAG = /^(v:[0-9a-f-]{36}|f:(8K|UHD|HD|SD|Audio)|m:[0-9a-f-]{36}:(8K|UHD|HD|SD|Audio))$/;
export const variantTag = (variantId: string) => `v:${variantId}`;
export const formatTag = (c: FormatClass) => `f:${c}`;
export const pairTag = (variantId: string, c: FormatClass) => `m:${variantId}:${c}`;

/** Splits a `masters` filter into its groups; unknown values are dropped. */
export function masterFilterGroups(values: readonly string[]): { variants: string[]; formats: string[]; pairs: string[] } {
  const ok = [...new Set(values.filter((v) => MASTER_TAG.test(v)))];
  return { variants: ok.filter((v) => v.startsWith('v:')), formats: ok.filter((v) => v.startsWith('f:')), pairs: ok.filter((v) => v.startsWith('m:')) };
}

/** The summary from an asset's tags (null when the asset has no tags: no media, or not counted yet). */
export function masterSummaryOf(tags: readonly string[] | null | undefined): MasterSummaryDTO | null {
  if (!tags?.length) return null;
  const formats = FORMAT_CLASSES.filter((c) => tags.includes(`f:${c}`));
  const variantIds = tags.filter((t) => t.startsWith('v:')).map((t) => t.slice(2));
  return { count: tags.filter((t) => t.startsWith('m:')).length, formats, variantIds };
}

/**
 * Which master an action reads:
 *   primary  the version's original (as before 1.2.0);
 *   variant  a variant (null: Programme), then a fixed format class, or (format null) the one closest to the output;
 *   master   one master by id (the dialogs' pickers).
 */
export const MasterChoiceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('primary') }),
  z.object({ type: z.literal('variant'), variantId: z.string().uuid().nullable().default(null), format: z.enum(FORMAT_CLASSES).nullable().default(null) }),
  z.object({ type: z.literal('master'), masterId: z.string().uuid() }),
]);
export type MasterChoice = z.infer<typeof MasterChoiceSchema>;
/** Copy to storage can also copy every master. */
export const MasterChoiceOrAllSchema = z.union([MasterChoiceSchema, z.object({ type: z.literal('all') })]);
export type MasterChoiceOrAll = z.infer<typeof MasterChoiceOrAllSchema>;

export const PRIMARY_MASTER: MasterChoice = { type: 'primary' };
/** Exports and Send to edit: Programme, the format closest to the output. */
export const DEFAULT_MASTER_CHOICE: MasterChoice = { type: 'variant', variantId: null, format: null };

/** The fields pickMaster reads (a MasterDTO, or the API's rows). */
export interface PickableMaster {
  id: string;
  primary: boolean;
  variant: { id: string; key: string | null };
  format: { formatClass: FormatClass; width: number | null; height: number | null };
  createdAt: string;
  /** A master's attributes (the API's rows); one marked replacedBy is never picked. */
  attributes?: { replacedBy?: unknown } | null;
}

/** False for a master being replaced (attributes.replacedBy): it is removed in the same transaction. */
export function isLiveMaster(m: { attributes?: { replacedBy?: unknown } | null }): boolean {
  return !m.attributes?.replacedBy;
}

/**
 * The master a choice means, or null when the asset has none that fits. With a variant and no format, the master
 * closest to `closestTo` (an output size): the smallest at least as large (no upscaling), else the largest; without a
 * size, the primary when it is of that variant, else the largest. Ties: the primary, then the newest.
 */
export function pickMaster<M extends PickableMaster>(all: readonly M[], choice: MasterChoice | null | undefined, closestTo?: { width: number; height: number } | null): M | null {
  const masters = all.filter(isLiveMaster);
  const c = choice ?? DEFAULT_MASTER_CHOICE;
  if (c.type === 'primary') return masters.find((m) => m.primary) ?? null;
  if (c.type === 'master') return masters.find((m) => m.id === c.masterId) ?? null;
  const ofVariant = masters.filter((m) => (c.variantId ? m.variant.id === c.variantId : m.variant.key === PROGRAMME_VARIANT_KEY));
  const candidates = c.format ? ofVariant.filter((m) => m.format.formatClass === c.format) : ofVariant;
  if (!candidates.length) return null;
  const px = (m: M) => (m.format.width ?? 0) * (m.format.height ?? 0);
  const tie = (a: M, b: M) => (a.primary === b.primary ? b.createdAt.localeCompare(a.createdAt) : a.primary ? -1 : 1);
  if (closestTo && closestTo.width > 0 && closestTo.height > 0) {
    const want = closestTo.width * closestTo.height;
    const enough = candidates.filter((m) => px(m) >= want).sort((a, b) => px(a) - px(b) || tie(a, b));
    if (enough.length) return enough[0]!;
  } else {
    const primary = candidates.find((m) => m.primary);
    if (primary) return primary;
  }
  return [...candidates].sort((a, b) => px(b) - px(a) || tie(a, b))[0]!;
}

/**
 * The output size pickMaster compares masters with for an export preset: a broadcast preset's frame, a web preset's
 * largest short side as 16:9; null for the original format and unknown presets (transcode profiles).
 */
export function presetOutputSize(preset: string): { width: number; height: number } | null {
  const p = (EXPORT_PRESETS as Record<string, { frame?: { width: number; height: number }; maxShortSide?: number }>)[preset];
  if (!p) return null;
  if (p.frame) return p.frame;
  if (p.maxShortSide) return { width: Math.round((p.maxShortSide * 16) / 9), height: p.maxShortSide };
  return null;
}

/** "Textless · UHD 2160p25 · ProRes 422 HQ" */
export function masterLabel(m: { variant: { name: string }; format: { label: string } }): string {
  return `${m.variant.name} · ${m.format.label}`;
}

/* ---------------------------------------------------------------- same content */

export interface ContentFacts {
  mediaKind: 'video' | 'audio' | 'image' | 'graphic' | null;
  durationTicks: number | null;
  frameRate: Rational | null;
}

/**
 * Why a file is not the same content as the primary master, or null when it is: the same duration to within one frame
 * of the primary (only when the variant requires the same length), and a frame rate equal to the primary's or twice or
 * half of it (50p of a 25i programme). A file without a picture (an audio mix) is checked only for its duration.
 */
export function sameContentProblem(primary: ContentFacts, candidate: ContentFacts, variant: { name: string; sameLengthRequired: boolean }): string | null {
  if (candidate.mediaKind !== 'video' && candidate.mediaKind !== 'audio') return 'A master must be video or audio';
  if (primary.mediaKind === 'audio' && candidate.mediaKind === 'video') return 'The primary master is audio only; a master with a picture is different content';
  const pr = primary.frameRate;
  const cr = candidate.frameRate;
  if (candidate.mediaKind === 'video' && pr && cr) {
    const ratio = (cr.num / cr.den) / (pr.num / pr.den);
    const near = (x: number) => Math.abs(ratio - x) < 0.001;
    if (!near(1) && !near(2) && !near(0.5)) return `Its frame rate (${frameRateLabel(cr)}) does not fit the primary master’s (${frameRateLabel(pr)})`;
  }
  if (variant.sameLengthRequired && primary.durationTicks && candidate.durationTicks != null) {
    const tolerance = ticksPerFrame(pr ?? cr ?? { num: 25, den: 1 });
    if (Math.abs(candidate.durationTicks - primary.durationTicks) > tolerance) {
      const s = (t: number) => (t / 705_600_000).toFixed(2);
      return `${variant.name} must be as long as the primary master to within a frame (${s(candidate.durationTicks)} s, the primary is ${s(primary.durationTicks)} s)`;
    }
  }
  return null;
}

/* ---------------------------------------------------------------- API bodies */

/** What happens when the asset already has a master of the same variant and format class. */
export const MASTER_CONFLICTS = ['refuse', 'replace', 'skip'] as const;
export type MasterConflict = (typeof MASTER_CONFLICTS)[number];

/**
 * POST /api/v1/assets/:id/masters/uploads: start "Add master…" (a resumable upload into the asset's current version).
 * replace: an existing master of the same variant and format is kept until the new one is verified, then removed.
 */
export const AddMasterBody = z.object({
  filename: z.string().min(1).max(500),
  sizeBytes: z.number().int().positive(),
  variantId: z.string().uuid().nullable().default(null),
  onConflict: z.enum(MASTER_CONFLICTS).default('refuse'),
});
export type AddMasterBody = z.infer<typeof AddMasterBody>;

/** PATCH /api/v1/assets/:id/masters/:masterId: Edit variant (audited). */
export const MasterPatchBody = z.object({ variantId: z.string().uuid() });

/** Error codes of the masters routes and of every route that takes a master. */
export const MASTER_ERRORS = {
  DIFFERENT_CONTENT: 'DIFFERENT_CONTENT',
  MASTER_EXISTS: 'MASTER_EXISTS',
  NO_SUCH_MASTER: 'NO_SUCH_MASTER',
  UNKNOWN_VARIANT: 'UNKNOWN_VARIANT',
  PRIMARY_IN_USE: 'PRIMARY_IN_USE',
  VARIANT_IN_USE: 'VARIANT_IN_USE',
} as const;

/** The domain event of a new master (the Master added trigger listens to it). */
export const MASTER_ADDED_EVENT = 'asset.master_added';
/** A master's variant changed or a master was removed (payload { assetId, versionId, masterId }); no trigger listens to it. */
export const MASTERS_CHANGED_EVENT = 'asset.masters_changed';
export interface MasterAddedPayload {
  assetId: string;
  versionId: string;
  masterId: string;
  variantId: string;
  variantName: string;
  variantCode: string;
  formatClass: FormatClass;
  formatLabel: string;
  source: MasterSource;
  /** The master it replaced, if any. */
  replaced: string | null;
}
